import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Col, Input, Popconfirm, Row, Table, Select, TablePaginationConfig } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ClientsApi } from 'api/api';
import { ClientBriefDto, ClientType, ProjectDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import { ClientForm } from './ClientForm';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { CustomTableStyleWrapper } from 'pages/style';
import { AutoComplete } from 'components/autoComplete/autoComplete';
import { ProjectsApi } from 'api/api';

const { Option } = Select;
const clientsApi = new ClientsApi();
const projectsApi = new ProjectsApi();

type ModalState = {
  visible: boolean;
  editMode: boolean;
  selectedClient: ClientBriefDto | null;
};

export const ClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ClientBriefDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    selectedClient: null,
  });
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    projectId: undefined as number | undefined,
    type: undefined as ClientType | undefined,
    filterType: -1,
  });

  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      console.log(query);
      const response = await clientsApi.apiClientsGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      openNotificationWithIcon('error', t('clients:fetch-error', 'Failed to fetch clients'));
    } finally {
      setLoading(false);
    }
  }, [query, t]);

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1, isActive: true });
      setProjects(response.data.items || []);
    } catch (err) {
      openNotificationWithIcon('error', t('projects:fetch-error', 'Failed to fetch projects'));
    } finally {
      setProjectsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleViewDetails = (id: number) => {
    navigate(`/clients/${id}`);
  };

  const columns = [
    {
      title: t('clients:name', 'Name'),
      key: 'name',
      render: (record: ClientBriefDto) => (
        <span>{record.type === ClientType.NUMBER_0 ? record.name : record.companyName}</span>
      ),
    },
    {
      title: t('clients:type', 'Type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: ClientType) => (
        <span>
          {type === ClientType.NUMBER_0 ? t('clients:individual', 'Individual') : t('clients:company', 'Company')}
        </span>
      ),
    },
    {
      title: t('clients:personalId', 'Personal ID'),
      dataIndex: 'personalId',
      key: 'personalId',
      render: (personalId: string | null) => <span>{personalId || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:companyRegistrationId', 'Company Registration ID'),
      dataIndex: 'companyRegistrationId',
      key: 'companyRegistrationId',
      render: (registrationId: string | null) => <span>{registrationId || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:taxNumber', 'Tax Number'),
      dataIndex: 'taxNumber',
      key: 'taxNumber',
      render: (taxNumber: string | null) => <span>{taxNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:email', 'Email'),
      dataIndex: 'email',
      key: 'email',
      render: (email: string | null) => <span>{email || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:phone', 'Phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string | null) => <span>{phoneNumber || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('clients:address', 'Address'),
      dataIndex: 'address',
      key: 'address',
      render: (address: string | null) => <span>{address || t('global:no-data', 'N/A')}</span>,
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 150,
      render: (record: ClientBriefDto) => (
        <div className="table-actions">
          {hasPermission('clients:view') && (
            <Button
              className="btn-icon"
              type="primary"
              shape="circle"
              onClick={() => handleViewDetails(record.id as number)}
            >
              <FeatherIcon icon="eye" size={16} />
            </Button>
          )}
          {hasPermission('clients:edit') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEdit(record)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('clients:delete') && (
            <Popconfirm
              title={t('clients:delete-confirm', 'Are you sure you want to delete this client?')}
              onConfirm={() => handleDelete(record.id as number)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const handleTypeFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      filterType: value,
      type: value === -1 ? undefined : (value as ClientType),
      pageNumber: 1,
      pageSize: 10,
    }));
  };

  const handleProjectFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      projectId: value === -1 ? undefined : value,
      pageNumber: 1,
    }));
  };

  const handleEdit = (client: ClientBriefDto) => {
    setModalState({
      visible: true,
      editMode: true,
      selectedClient: client,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await clientsApi.apiClientsIdDelete({ id });
      openNotificationWithIcon('success', t('clients:deleted', 'Client deleted successfully'));
      fetchClients();
    } catch (err) {
      openNotificationWithIcon('error', t('clients:delete-error', 'Failed to delete client'));
    }
  };

  const handleModalSubmit = async (): Promise<void> => {
    resetModalState();
    await fetchClients();
  };

  const resetModalState = () => {
    setModalState({ visible: false, editMode: false, selectedClient: null });
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('clients:title', 'Clients')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('clients:title', 'Clients')}
              data={data}
              columns={['name', 'email', 'phoneNumber', 'companyName']}
            />,
            hasPermission('clients:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, selectedClient: null })}
              >
                <FeatherIcon icon="plus" size={16} />
                {t('clients:add', 'Add Client')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('clients:search', 'Search clients...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('projects:project-filter', 'Project:')}</span>
                        <Select
                          style={{ width: 200 }}
                          onChange={handleProjectFilterChange}
                          value={query.projectId ?? -1}
                          loading={projectsLoading}
                        >
                          <Select.Option value={-1}>{t('global:all', 'All')}</Select.Option>
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('clients:type', 'Type:')}</span>
                        <Select
                          style={{ width: 200 }}
                          placeholder={t('clients:type-filter', 'Type:')}
                          allowClear
                          value={query.filterType}
                          onChange={handleTypeFilterChange}
                        >
                          <Option value={-1}>{t('global:all', 'All')}</Option>
                          <Option value={ClientType.NUMBER_0}>{t('clients:individual', 'Individual')}</Option>
                          <Option value={ClientType.NUMBER_1}>{t('clients:company', 'Company')}</Option>
                        </Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <CustomTableStyleWrapper>
            <TableWrapper>
              <Table
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={{
                  current: query.pageNumber,
                  pageSize: query.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 50, 100, 1000],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                  onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                }}
              />
            </TableWrapper>
          </CustomTableStyleWrapper>
        </Cards>
      </Main>

      {modalState.visible && (
        <ClientForm
          visible={modalState.visible}
          editMode={modalState.editMode}
          client={modalState.selectedClient}
          onCancel={() => resetModalState()}
          onSuccess={handleModalSubmit}
        />
      )}
    </>
  );
};

export default ClientsPage;
